import { getNestedValue } from '../util';
import { format, parse, parseISO } from 'date-fns';
import { MonthField } from '@pxtra-client-lib-legacy/gen';
import { makeAutoObservable } from 'mobx';

export class ViewModelBasedOn<TModel> {
  model: TModel;
  constructor(model: TModel) {
    this.model = model;
  }
}

export type Entity<TId> = {
  id?: TId;
};

export class ValueCache {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private values: Map<string, any>;

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.values = new Map<string, any>();
  }

  get<T>(name: string, initValue: () => T) {
    if (this.values.has(name) == false) {
      this.values.set(name, initValue());
    }
    return this.values.get(name) as T;
  }

  public clear() {
    this.values.clear();
  }
}

export abstract class LazyViewModelBasedOn<
  TPreviewModel extends Entity<TId>,
  TExtendedModel extends Entity<TId>,
  TId = string
> {
  constructor(
    protected preview?: TPreviewModel,
    protected extended?: TExtendedModel
  ) {}

  protected abstract _loadExtended(): Promise<TExtendedModel>;

  public async loadExtended() {
    if (this.preview?.id == undefined) {
      console.error("Loading extended model failed because preview isn't set correctly!");
      return;
    }
    this.extended ??= await this._loadExtended();
  }

  protected getValueByPath(path: string[], previewPath?: string[]) {
    return this.getValue(
      (extended) => getNestedValue(extended, path),
      (preview) => getNestedValue(preview, previewPath ?? path)
    );
  }

  protected getValue(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fromExtened: (model: TExtendedModel) => any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fromPreview: (model: TPreviewModel) => any
  ) {
    let value;
    if (this.extended) {
      value = fromExtened(this.extended);
    }

    if (!value && this.preview) {
      value = fromPreview(this.preview);
    }
    return value;
  }
}

export class MoneyViewModel {
  constructor(public cents: number) {}

  format(decimal = true, currency = 'EUR') {
    return (this.cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: (decimal ?? 1) ? 2 : 0
    });
  }
}

export class DateViewModel {
  constructor(public date: Date) {}

  format(formatString = 'dd.MM.yyyy') {
    return format(this.date, formatString);
  }

  get monthName() {
    return format(this.date, 'MMMM');
  }

  static fromISO(dateString: string) {
    return new DateViewModel(parseISO(dateString));
  }

  static fromDjango(dateString: string) {
    return new DateViewModel(parse(dateString, 'yyyy-MM-dd', new Date()));
  }

  static fromMonthField(field: MonthField) {
    return new DateViewModel(new Date(field.year, field.month - 1));
  }
}

export class LoadingViewModel {
  private _is: boolean;

  get is() {
    return this._is;
  }

  start() {
    this._is = true;
  }

  stop() {
    this._is = false;
  }
  constructor(isLoading = false) {
    this._is = isLoading;
    makeAutoObservable(this);
  }
}

export type ViewModelProps<TViewModel> = {
  viewModel: TViewModel;
};
